import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../../Context/ThemeContext';
import { PlayerHandler } from '../PlayerHandler';

interface PlayerDevicesProps {
  userId: string;
}

interface DeviceItem {
  deviceName?: string;
  model?: string;
  manufacturer?: string;
  operatingSystem?: string;
  currentAppVersion?: string;
  screenSize?: string;
  resolution?: string;
  deviceId?: string;
  lastAccessDate?: string;
}

const PlayerDevices: React.FC<PlayerDevicesProps> = ({ userId }) => {
  const { theme, isMobile } = useTheme();
  const [devices, setDevices] = useState<DeviceItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const handler = new PlayerHandler();
        const response = await handler.getPlayerDevices(userId);
        if (response?.success) {
          setDevices(response.devices || []);
        }
      } catch (err) {
        console.error('Error loading devices:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [userId]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const styles: Record<string, React.CSSProperties> = {
    container: {
      background: theme.card,
      borderRadius: '12px',
      padding: isMobile ? '16px' : '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    title: { fontSize: '18px', fontWeight: 700, color: theme.text, marginBottom: '12px' },
    grid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: '12px',
    },
    card: {
      padding: '12px',
      borderRadius: '8px',
      background: theme.inputBg || theme.tableHeader,
      border: `1px solid ${theme.border}`,
    },
    deviceName: { fontSize: '14px', fontWeight: 600, color: theme.text, marginBottom: '4px' },
    detail: { fontSize: '12px', color: theme.textSecondary, marginBottom: '2px' },
    empty: { fontSize: '13px', color: theme.textSecondary, fontStyle: 'italic' as const },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.title}>Devices</div>
        <div style={styles.empty}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.title}>Devices ({devices.length})</div>
      {devices.length === 0 ? (
        <div style={styles.empty}>No devices registered</div>
      ) : (
        <div style={styles.grid}>
          {devices.map((d, i) => (
            <div key={i} style={styles.card}>
              <div style={styles.deviceName}>{d.deviceName || d.model || 'Unknown Device'}</div>
              <div style={styles.detail}>{d.manufacturer} {d.model}</div>
              <div style={styles.detail}>OS: {d.operatingSystem || '-'}</div>
              <div style={styles.detail}>App: {d.currentAppVersion || '-'}</div>
              <div style={styles.detail}>Screen: {d.screenSize || '-'} ({d.resolution || '-'})</div>
              <div style={styles.detail}>Last seen: {formatDate(d.lastAccessDate)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlayerDevices;
